import { useDroppable } from '@dnd-kit/core';
import {
  Card,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Box,
  Checkbox,
  Select,
} from '@mantine/core';
import { AlertCircle, Users } from 'react-feather';
import type { Participant } from '../../../types';
import type { GroupWithMembers } from '../../../services/api/groups';
import {
  DraggableParticipantCard,
  participantToDraggable,
  type DraggableParticipantData,
} from './DraggableParticipantCard';
import classes from './UnassignedParticipantsSection.module.css';

interface UnassignedParticipantsSectionProps {
  participants: Participant[];
  groups: GroupWithMembers[];
  selectedParticipants: Set<string>;
  onSelectParticipant: (participant: DraggableParticipantData, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  onAssignToGroup: (participant: DraggableParticipantData) => void;
  onBulkAssign: (groupId: string) => void;
  selectionMode?: boolean;
}

export function UnassignedParticipantsSection({
  participants,
  groups,
  selectedParticipants,
  onSelectParticipant,
  onSelectAll,
  onAssignToGroup,
  onBulkAssign,
  selectionMode = false,
}: UnassignedParticipantsSectionProps) {
  // Make unassigned section a drop target (for removing from groups)
  const { isOver, setNodeRef } = useDroppable({
    id: 'unassigned',
    data: { isUnassigned: true },
  });

  if (participants.length === 0) {
    return null;
  }

  const allSelected = participants.length > 0 && participants.every(p => selectedParticipants.has(p.user_id));
  const someSelected = participants.some(p => selectedParticipants.has(p.user_id));

  // Group options for bulk assign
  const groupOptions = groups.map((g) => ({
    value: g.id,
    label: `${g.name} (${g.member_count})`,
  }));

  return (
    <Card
      ref={setNodeRef}
      withBorder
      className={`${classes.card} ${isOver ? classes.dropOver : ''}`}
    >
      {/* Drop indicator for removing from groups */}
      {isOver && (
        <Box className={classes.dropIndicator}>
          <Text size="sm" fw={500}>Drop to remove from group</Text>
        </Box>
      )}

      {/* Header */}
      <Group justify="space-between" mb="md">
        <Group gap="sm">
          <ThemeIcon size={40} radius="xl" color="warningAmber" variant="light">
            <AlertCircle size={20} />
          </ThemeIcon>
          <Box>
            <Text fw={600} size="sm">Unassigned Participants</Text>
            <Text size="13px" c="dimmed">
              {participants.length} participant{participants.length !== 1 ? 's' : ''} need{participants.length === 1 ? 's' : ''} to be assigned to a group
            </Text>
          </Box>
        </Group>

        {/* Bulk actions */}
        {selectionMode && selectedParticipants.size > 0 && (
          <Group gap="sm">
            <Text size="sm" c="dimmed">
              {selectedParticipants.size} selected
            </Text>
            <Select
              placeholder="Assign to..."
              data={groupOptions}
              onChange={(value) => value && onBulkAssign(value)}
              clearable={false}
              w={180}
              size="sm"
            />
          </Group>
        )}
      </Group>

      {/* Selection controls */}
      {selectionMode && participants.length > 1 && (
        <Group gap="xs" mb="sm">
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            onChange={(e) => onSelectAll(e.target.checked)}
            label={allSelected ? 'Deselect all' : 'Select all'}
            size="sm"
          />
        </Group>
      )}

      {/* Participants List */}
      <Stack gap="sm">
        {participants.map((participant) => (
          <DraggableParticipantCard
            key={participant.id}
            participant={participantToDraggable(participant)}
            selected={selectedParticipants.has(participant.user_id)}
            onSelect={onSelectParticipant}
            selectionMode={selectionMode}
            onAssignToGroup={onAssignToGroup}
            colorScheme="warningAmber"
          />
        ))}
      </Stack>

      {/* Quick tip */}
      {!selectionMode && participants.length > 0 && (
        <Box mt="md" p="sm" className={classes.tipBox}>
          <Group gap="xs">
            <Users size={14} />
            <Text size="xs" c="dimmed">
              Drag participants to a group, or click the menu to assign individually
            </Text>
          </Group>
        </Box>
      )}
    </Card>
  );
}

export default UnassignedParticipantsSection;
